import { FormBlockInstance, FormDefinition } from '../api-types';
import {
  DATE_PICKER_TYPE,
  DATE_PICKER_TYPE_VERSION,
  isDatePickerAnswered,
  readDatePickerConfig
} from './datePicker';
import { DatePickerBlock } from './DatePickerBlock';
import {
  isItemScaleAnswered,
  ITEM_SCALE_TYPE,
  ITEM_SCALE_TYPE_VERSION,
  readItemScaleConfig
} from './itemScale';
import { ItemScaleBlock } from './ItemScaleBlock';
import {
  isMagnitudeDialAnswered,
  MAGNITUDE_DIAL_TYPE,
  MAGNITUDE_DIAL_TYPE_VERSION
} from './magnitudeDial';
import { MagnitudeDialBlock } from './MagnitudeDialBlock';
import {
  isMultiSelectAnswered,
  MULTI_SELECT_TYPE,
  MULTI_SELECT_TYPE_VERSION,
  readMultiSelectConfig
} from './multiSelect';
import { MultiSelectBlock } from './MultiSelectBlock';
import {
  isNumberStepperAnswered,
  NUMBER_STEPPER_TYPE,
  NUMBER_STEPPER_TYPE_VERSION,
  readNumberStepperConfig
} from './numberStepper';
import { NumberStepperBlock } from './NumberStepperBlock';
import { isRatingAnswered, RATING_TYPE, RATING_TYPE_VERSION } from './rating';
import { RatingBlock } from './RatingBlock';
import {
  isShiftFitAnswered,
  readShiftFitConfig,
  SHIFT_FIT_TYPE,
  SHIFT_FIT_TYPE_VERSION
} from './shiftFit';
import { ShiftFitBlock } from './ShiftFitBlock';
import {
  isSingleSelectAnswered,
  readSingleSelectConfig,
  SINGLE_SELECT_TYPE,
  SINGLE_SELECT_TYPE_VERSION
} from './singleSelect';
import { SingleSelectBlock } from './SingleSelectBlock';
import { STATEMENT_TYPE, STATEMENT_TYPE_VERSION } from './statement';
import { StatementBlock } from './StatementBlock';
import {
  isTextAreaAnswered,
  readTextAreaConfig,
  TEXT_AREA_TYPE,
  TEXT_AREA_TYPE_VERSION
} from './textArea';
import { TextAreaBlock } from './TextAreaBlock';
import {
  isTextInputAnswered,
  readTextInputConfig,
  TEXT_INPUT_TYPE,
  TEXT_INPUT_TYPE_VERSION
} from './textInput';
import { TextInputBlock } from './TextInputBlock';
import { BlockRendererEntry } from './types';

/**
 * Every block type this app build can render, keyed by `type@typeVersion`.
 *
 * The version is part of the key on purpose. A type's contract never changes in place: a changed
 * `single_select` ships as version 2 alongside version 1, so forms published against version 1 keep
 * rendering exactly as they were authored. That is also why an unknown key is a real possibility
 * rather than a bug — pro-core can be ahead of the app bundle someone is running.
 *
 * Adding a block type means adding one entry here. Nothing else in the runner changes.
 */
const ENTRIES: BlockRendererEntry[] = [
  {
    type: SINGLE_SELECT_TYPE,
    typeVersion: SINGLE_SELECT_TYPE_VERSION,
    kind: 'INPUT',
    component: SingleSelectBlock,
    isAnswered: (answer, block) =>
      isSingleSelectAnswered(answer, readSingleSelectConfig(block).options)
  },
  {
    type: MULTI_SELECT_TYPE,
    typeVersion: MULTI_SELECT_TYPE_VERSION,
    kind: 'INPUT',
    component: MultiSelectBlock,
    isAnswered: (answer, block) =>
      isMultiSelectAnswered(answer, readMultiSelectConfig(block))
  },
  {
    type: RATING_TYPE,
    typeVersion: RATING_TYPE_VERSION,
    kind: 'INPUT',
    component: RatingBlock,
    // No config needed: a chosen level is answered, and the ordinal is the server's business.
    isAnswered: answer => isRatingAnswered(answer)
  },
  {
    type: DATE_PICKER_TYPE,
    typeVersion: DATE_PICKER_TYPE_VERSION,
    kind: 'INPUT',
    component: DatePickerBlock,
    isAnswered: (answer, block) =>
      isDatePickerAnswered(answer, readDatePickerConfig(block))
  },
  {
    type: TEXT_INPUT_TYPE,
    typeVersion: TEXT_INPUT_TYPE_VERSION,
    kind: 'INPUT',
    component: TextInputBlock,
    isAnswered: (answer, block) =>
      isTextInputAnswered(answer, readTextInputConfig(block))
  },
  {
    type: NUMBER_STEPPER_TYPE,
    typeVersion: NUMBER_STEPPER_TYPE_VERSION,
    kind: 'INPUT',
    component: NumberStepperBlock,
    isAnswered: (answer, block) =>
      isNumberStepperAnswered(answer, readNumberStepperConfig(block))
  },
  {
    type: STATEMENT_TYPE,
    typeVersion: STATEMENT_TYPE_VERSION,
    kind: 'CONTENT',
    component: StatementBlock,
    // Nothing is collected, so there is nothing to be answered. Reporting `true` keeps progress
    // honest: a statement is never an outstanding item, and counting it as unanswered would leave a
    // finished form showing less than 100%. The runner only asks this of blocks it gates on, but
    // the entry must answer coherently either way.
    isAnswered: () => true
  },
  {
    type: TEXT_AREA_TYPE,
    typeVersion: TEXT_AREA_TYPE_VERSION,
    kind: 'INPUT',
    component: TextAreaBlock,
    isAnswered: (answer, block) =>
      isTextAreaAnswered(answer, readTextAreaConfig(block))
  },
  {
    type: SHIFT_FIT_TYPE,
    typeVersion: SHIFT_FIT_TYPE_VERSION,
    kind: 'INPUT',
    component: ShiftFitBlock,
    isAnswered: (answer, block) =>
      isShiftFitAnswered(answer, readShiftFitConfig(block))
  },
  {
    type: ITEM_SCALE_TYPE,
    typeVersion: ITEM_SCALE_TYPE_VERSION,
    kind: 'INPUT',
    component: ItemScaleBlock,
    isAnswered: (answer, block) =>
      isItemScaleAnswered(answer, readItemScaleConfig(block))
  },
  {
    type: MAGNITUDE_DIAL_TYPE,
    typeVersion: MAGNITUDE_DIAL_TYPE_VERSION,
    kind: 'INPUT',
    // No config needed: any stored number is an answer, and which numbers are legal is the server's
    // business — the dial can only produce values it accepts.
    isAnswered: answer => isMagnitudeDialAnswered(answer),
    component: MagnitudeDialBlock
  }
];

const keyOf = (type: string, typeVersion: number) => `${type}@${typeVersion}`;

const REGISTRY = new Map<string, BlockRendererEntry>(
  ENTRIES.map(entry => [keyOf(entry.type, entry.typeVersion), entry])
);

export const findRenderer = (
  block: FormBlockInstance
): BlockRendererEntry | undefined =>
  REGISTRY.get(keyOf(block.type, block.typeVersion));

/**
 * The blocks this build cannot render.
 *
 * Called before the form is shown, because a stale client must never silently skip a block: a form
 * that quietly drops a question would be submitted as complete while missing an answer nobody knows
 * was asked. If this returns anything, the whole form gives way to a "please update" screen — a
 * refresh, not a partial form (catalog §15).
 */
export const unsupportedBlocksOf = (
  definition: FormDefinition | null
): FormBlockInstance[] =>
  (definition?.blocks ?? []).filter(block => !findRenderer(block));
